<template>
  <div class="brand">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true">
        <use xlink:href="#icon-pinpai"></use>
      </svg>
    </breadcrumbs>
    <reFetch
      :key="brand"
      type="brand"
      :refetch="reFetchFunction"
      :activeTab="Number(activeTab)"
    />
    <artmisads-segmented-tabs
      v-model="activeTab"
      :items="programConfig"
      :disabled="load"
    />

    <div class="table">
      <div class="table-head">
        <a-space>
          <ButtonGroups
            :pagination="pagination"
            :tableData="dataSource"
            @pageChange="onPageChange"
          />
        </a-space>

        <a-space class="filters">
          <a-input-search
            v-model:value="keywords"
            :placeholder="$t('seller.common.searchPlaceholder')"
            allow-clear
            @search="onSearch"
            @enter="onSearch"
          >
          </a-input-search>

          <div class="statusFilter">
            <span style="margin-right: 8px">{{ $t('seller.common.status') }}</span>
            <artmisads-select
              v-model:value="status"
              style="width: 130px"
              @change="onStatusChange"
              trigger="click"
            >
              <a-select-option value="all">{{ $t('seller.common.all') }}</a-select-option>
              <a-select-option value="ACTIVE">{{ $t('seller.common.active') }}</a-select-option>
              <a-select-option value="INACTIVE">{{ $t('seller.common.inactive') }}</a-select-option>
            </artmisads-select>
          </div>
        </a-space>
      </div>
      <artmisads-table
        v-model:pagination="pagination"
        :columns="columns"
        :data-source="dataSource"
        :change="onTableChange"
        :loading="load"
        :noData="noData"
        :scroll="{x:activeTab == '0' ? 1600 : 1900 }"
      >
        <template #emptyText>
          <div>
            <svg
              width="64"
              height="41"
              viewBox="0 0 64 41"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="translate(0 1)" fill="none" fill-rule="evenodd">
                <ellipse
                  fill="#f5f5f5"
                  cx="32"
                  cy="33"
                  rx="32"
                  ry="7"
                ></ellipse>
                <g fill-rule="nonzero" stroke="#d9d9d9">
                  <path
                    d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"
                  ></path>
                  <path
                    d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                    fill="#fafafa"
                  ></path>
                </g>
              </g>
            </svg>
          </div>
          <p class="ant-empty-description">
            {{ $t('seller.common.noBrandsData') }}
          </p>
          <p class="ant-empty-description">
            {{ $t('seller.common.clickUpdateRetry') }}
          </p>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'amazonBrandName'">
            <div class="asin-vc-icon">
              <a
                class="brand-title"
                target="_blank"
                :href="`/seller/brand/detail/${record.id}`"
              >
                <div
                  class="brand-img-show"
                  :style="record?.logoImageUrl ? { background: '#ffffff' } : ''"
                >
                  <img :src="record.logoImageUrl" v-if="record.logoImageUrl" />
                  <span class="brand-name" v-else>{{
                    record.amazonBrandName
                  }}</span>
                </div>
                <div class="brand-font">
                  <span style="text-decoration: underline">{{
                    record.amazonBrandName
                  }}</span>
                  <span v-if="record.hasDuplicate"
                    >({{ record.amazonProfileAccountInfoName }})</span
                  >
                </div>
              </a>
              <VcIcon
                v-if="record.amazonProfileAccountInfoType == 'vendor'"
                type="Brands"
              />
            </div>
          </template>
          <template v-if="column.key === 'status'">
            <div>
              <Status
                :type="record.status.toLowerCase()"
                v-if="activeTab == '0'"
                :noBorder="true"
                :text="record.status == 'active' ? $t('seller.common.active') : $t('seller.common.inactive')"
              />
              <Status
                :type="record.associateStatus.toLowerCase()"
                v-if="activeTab == '1'"
                :noBorder="true"
                :text="
                  record.associateStatus == 'active' ? $t('seller.common.active') : $t('seller.common.inactive')
                "
              />
            </div>
            <div class="time" v-if="record.timeMonth">
              <a-popover placement="bottom">
                <template #content>
                  <div class="contentBox">
                    <div class="popoverContent">
                      <p>
                        {{
                          $t('seller.brandsPage.statusChangePopover', {
                            status: nextScheduledStatus(record),
                            date: record.timeMonthNameEOD,
                          })
                        }}
                      </p>
                    </div>
                    <div class="cancelBtn" @click="cancelBrand(record)">
                      {{ $t('seller.common.cancel') }}
                    </div>
                  </div>
                </template>
                <template #title>
                  <span></span>
                </template>
                <div class="time-mid">
                  <Icon name="Frame" />
                  <span class="time-month">{{
                    $t('seller.brandsPage.untilEod', { month: record.timeMonth })
                  }}</span>
                  <Icon name="a-error-tianchong5" class="close-icon" />
                </div>
              </a-popover>
            </div>
          </template>
          <template v-if="column.dataIndex === 'activeProductCount'">
            <a class="table-font" @click="goProductPage(record, 'active')">
              {{
                activeTab == '0'
                  ? record.activeProductCount
                  : record.associateActiveProductCount
              }}
            </a>
          </template>
          <template v-if="column.dataIndex === 'inactiveProductCount'">
            <a class="table-font" @click="goProductPage(record, 'inactive')">
              {{
                activeTab == '0'
                  ? record.inactiveProductCount
                  : record.associateInactiveProductCount
              }}
            </a>
          </template>
          <template v-else-if="column.dataIndex === 'ccAuthorizationStatus'">
            <div class="ccAuthorizationStatus">
              <Status
                :type="record?.ccAuthorizationStatus?.toLowerCase()"
                :noBorder="true"
                :text="capitalize(record?.ccAuthorizationStatus?.toLowerCase())"
                v-if="record?.ccAuthorizationStatus"
              />
            </div>
          </template>
          <template v-if="column.key === 'publisher'">
            <a
              class="table-font"
              @click="openPublisherModal(record)"
              v-if="record.publisherAssociateCount"
              >{{ record.publisherAssociateCount }}</a
            >
            <span v-else>--</span>
          </template>
          <template v-if="column.key === 'action'">
            <div class="action-btn">
            
              <artmisads-button
                :isSquare="true"
                type="primary"
                size="small"
                @click="onOpenModal(record, 'active')"
                v-if="
                  record.status == 'inactive' &&
                  activeTab == '0' &&
                  !record.timeMonth
                "
              >
                {{ $t('seller.brandsPage.activate') }}
              </artmisads-button>
              <a-popover
                placement="left"
                v-if="
                  record.status == 'inactive' &&
                  activeTab == '0' &&
                  record.timeMonth
                "
              >
                <template #content>
                  <div class="contentBox">
                    <p class="popoverContent">
                      {{
                        $t('seller.brandsPage.cancelScheduledFirst', {
                          month: record.timeMonthNameAll,
                        })
                      }}
                    </p>
                    <div class="cancelBtn" @click="cancelBrand(record)">
                      {{ $t('seller.common.cancel') }}
                    </div>
                  </div>
                </template>
                <template #title>
                  <span></span>
                </template>
                <artmisads-button
                  disabled
                  :isSquare="true"
                  type="primary"
                  size="small"
                >
                  {{ $t('seller.brandsPage.activate') }}
                </artmisads-button>
              </a-popover>

              <artmisads-button
                :isSquare="true"
                type="primary"
                size="small"
                @click="onOpenModal(record, 'active')"
                v-if="
                  record.associateStatus == 'inactive' &&
                  activeTab == '1' &&
                  !record.timeMonth
                "
              >
                {{ $t('seller.brandsPage.activate') }}
              </artmisads-button>
              <a-popover
                placement="bottom"
                v-if="
                  record.associateStatus == 'inactive' &&
                  activeTab == '1' &&
                  record.timeMonth
                "
              >
                <template #content>
                  <div class="contentBox">
                    <p class="popoverContent">
                      {{
                        $t('seller.brandsPage.cancelScheduledFirst', {
                          month: record.timeMonthNameAll,
                        })
                      }}
                    </p>
                    <div class="cancelBtn" @click="cancelBrand(record)">
                      {{ $t('seller.common.cancel') }}
                    </div>
                  </div>
                </template>
                <template #title>
                  <span></span>
                </template>
                <artmisads-button
                  disabled
                  :isSquare="true"
                  type="primary"
                  size="small"
                >
                  {{ $t('seller.brandsPage.activate') }}
                </artmisads-button>
              </a-popover>
              <artmisads-select-button
                v-if="
                  record.status == 'active' &&
                  activeTab == '0' &&
                  !record.timeMonth
                "
                size="small"
                @click="onOpenModal(record, 'deactive')"
              >
                {{ $t('seller.brandsPage.deactivate') }}
              </artmisads-select-button>
              <a-popover
                placement="left"
                v-if="
                  record.status == 'active' &&
                  activeTab == '0' &&
                  record.timeMonth
                "
              >
                <template #content>
                  <div class="contentBox">
                    <p class="popoverContent">
                      {{
                        $t('seller.brandsPage.cancelScheduledFirst', {
                          month: record.timeMonthNameAll,
                        })
                      }}
                    </p>
                    <div class="cancelBtn" @click="cancelBrand(record)">
                      {{ $t('seller.common.cancel') }}
                    </div>
                  </div>
                </template>
                <template #title>
                  <span></span>
                </template>
                <artmisads-select-button
                  disabled
                  size="small"
                  @click="onOpenModal(record, 'deactive')"
                >
                  {{ $t('seller.brandsPage.deactivate') }}
                </artmisads-select-button>
              </a-popover>

              <artmisads-select-button
                v-if="
                  record.associateStatus == 'active' &&
                  activeTab == '1' &&
                  !record.timeMonth
                "
                size="small"
                @click="onOpenModal(record, 'deactive')"
              >
                {{ $t('seller.brandsPage.deactivate') }}
              </artmisads-select-button>
              <a-popover
                placement="left"
                v-if="
                  record.associateStatus == 'active' &&
                  activeTab == '1' &&
                  record.timeMonth
                "
              >
                <template #content>
                  <div class="contentBox">
                    <p class="popoverContent">
                      {{
                        $t('seller.brandsPage.cancelScheduledFirst', {
                          month: record.timeMonthNameAll,
                        })
                      }}
                    </p>
                    <div class="cancelBtn" @click="cancelBrand(record)">
                      {{ $t('seller.common.cancel') }}
                    </div>
                  </div>
                </template>
                <template #title>
                  <span></span>
                </template>
                <artmisads-select-button
                  disabled
                  size="small"
                  @click="onOpenModal(record, 'deactive')"
                >
                  {{ $t('seller.brandsPage.deactivate') }}
                </artmisads-select-button>
              </a-popover>
                <artmisads-select-button
                v-if="activeTab == '0'"
                size="small"
                @click="onOpenModal(record, 'setCommission')"
              >
                {{ $t('seller.brandsPage.setCommission') }}
              </artmisads-select-button>
              <!-- <artmisads-select-button
                v-if="activeTab == '1'"
                size="small"
                @click="onOpenModal(record, 'setPublisher')"
              >
                Setup Commission
              </artmisads-select-button> -->
              <artmisads-select-button
                  v-if="activeTab == '1'&&isAcc"

                  size="small"
                  @click="handleChangeAuthorizationStatus(record)"
                >
                  {{ $t('seller.brandsPage.accAuthorization') }}
                </artmisads-select-button>
            </div>
          </template>
        </template>
      </artmisads-table>
    </div>

    <TableModal
      :configs="modalConfigs"
      :item="singleRecord"
      @cancel="onModalCancel"
      @ok="onModalOK"
    />
    <successfulPop
      :successInfo="successInfo"
      :configs="successfulPopConfigs"
      :item="singleRecord"
      @cancel="onUpAssociateCommissioModalCancel"
      @ok="onUpAssociateCommissioModalOK"
      @setUpAssociateCommission="setUpAssociateCommission"
    />
    <setPublicCommission
      :item="singleRecord"
      :type="'brand'"
      :showSetCommission="showSetCommission"
      :productList="dataSource"
      :selectedRowKeys="[]"
      :setShowSetCommission="setShowSetCommission"
      :getProductList="reFetchFunction"
      :selectSingleProduct="selectSingleProduct"
      :setSelectSingleProduct="setSelectSingleProduct"
    />
    <setUpPartnership ref="setUpPartnershipRef" />
    <PublishModal ref="publishModalRef" />
    <setPartnershipModal ref="partnershipModal" />
    <contextHolder />
  </div>
</template>
<script setup>
import i18n from '~/i18n'

import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, computed, reactive, onUnmounted, watch } from 'vue'
import { brandApi } from '@/server/index'
import breadcrumbs from '../breadcrumbs.vue'
import reFetch from './components/reFetch.vue'
import TableModal from './components/tableModal.vue'
import Status from '@/components/Status.vue'
import successfulPop from './components/successfulPop.vue'
import ButtonGroups from './components/buttonGroups.vue'
import { useSellerProductStore } from '@/storeData/sellerproduct'
import { useSellerBrandStore } from '@/storeData/sellerbrand'
import { message, notification } from 'ant-design-vue'
import setPublicCommission from '../product/setPublicCommission.vue'
import { useUserInfoStore } from '@/storeData'
import VcIcon from '~/components/VcIcon.vue'
import Icon from '@/components/Icon.vue'
import { useSystemStore } from '@/storeData/system'
import PublishModal from './components/publishModal.vue'
import { countBrandNames } from '~/utils'
import setUpPartnership from './components/setUpPartnership.vue'
import setPartnershipModal from '~/components/setPartnership.vue'
import { COUNTRY_TIME_TZ } from '~/contants/common.js'
import { capitalize } from '~/utils'
import moment from 'moment'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const user = useUserInfoStore()
const timezone = COUNTRY_TIME_TZ[user.userInfo.countryCode]
const partnershipModal = ref(null)
const systemStore = useSystemStore()
/** 暂时隐藏 Brands 页 Associate 标签页 */
const HIDE_ASSOCIATE_BRANDS_TAB = true
const programConfig = computed(() => {
  locale.value
  let src = systemStore.programConfig
  if (!src?.length) return src
  if (HIDE_ASSOCIATE_BRANDS_TAB) {
    src = src.filter((item) => item.key !== '1')
  }
  return src.map((item) => {
    if (item.key === '0') return { ...item, label: i18n.global.t('seller.common.amazonAttribution') }
    if (item.key === '1') return { ...item, label: i18n.global.t('seller.common.amazonAssociate') }
    return { ...item }
  })
})
const isAcc = computed(() => systemStore.isAcc)
const setUpPartnershipRef = ref(null)
const activeTab = ref('0')
const nextScheduledStatus = (record) => {
  locale.value
  const isCurrentlyActive =
    activeTab.value === '0'
      ? record.status === 'active'
      : record.associateStatus === 'active'
  return isCurrentlyActive
    ? t('seller.common.inactive')
    : t('seller.common.active')
}
const [api, contextHolder] = notification.useNotification()
const openNotification = (description) => {
  api.info({
    message: i18n.global.t('seller.common.notice'),
    description: description,
    placement: 'topRight',
    duration: 3,
  })
}

const breadcrumbsData = computed(() => ({
  title: t('seller.menu.brands'),
  des: t('seller.brand.pageDes'),
  color: '#FFD358',
  hasTitleIcon: true,
  links: [
    { name: t('seller.common.home'), path: '/seller/dashboard' },
    { name: t('seller.menu.brands'), path: '/seller/brand' },
  ],
}))
const successInfo = ref({})
const noData = ref(false)
const load = ref(true)
const productStore = useSellerProductStore()
const brandStore = useSellerBrandStore()
const userStore = useUserInfoStore()
const brandRouter = useRouter()
const route = useRoute()
const columns = computed(() => {
  locale.value
  let _columns = [
    {
      title: t('seller.brand.colBrand'),
      key: 'amazonBrandName',
      dataIndex: 'amazonBrandName',
      width: 400,
    },

    {
      title: t('seller.brand.colStatus'),
      key: 'status',
      dataIndex: 'status',
      width: 200,
    },
    {
      title: t('seller.brand.colAmazonSellerName'),
      key: 'amazonName',
      dataIndex: 'amazonName',
      width: 220,
    },
    {
      title: t('seller.brand.colActiveProducts'),
      key: 'activeProductCount',
      dataIndex: 'activeProductCount',
      width: 200,
    },
    {
      title: t('seller.brand.colInactiveProducts'),
      key: 'inactiveProductCount',
      dataIndex: 'inactiveProductCount',
      width: 200,
    },
    {
      title: t('seller.brand.colAction'),
      key: 'action',
      dataIndex: 'action',
      fixed: 'right',
      width: 160,
    },
  ]

  // 当activeTab为1时，添加publisher列并过滤掉amazonName列
  if (activeTab.value === '1') {
    const columnsWithPublisher = [
      {
        title: t('seller.brand.colBrand'),
        key: 'amazonBrandName',
        dataIndex: 'amazonBrandName',
        width: 520,
      },
      {
        title: t('seller.brand.colAssociateStatus'),
        key: 'status',
        dataIndex: 'status',
        width: 200,
      },
      {
        title: t('seller.brand.colAmazonSellerName'),
        key: 'amazonName',
        dataIndex: 'amazonName',
        width: 220,
      },

      {
        title: t('seller.brand.colActiveProducts'),
        key: 'activeProductCount',
        dataIndex: 'activeProductCount',
        width: 200,
      },
      {
        title: t('seller.brand.colInactiveProducts'),
        key: 'inactiveProductCount',
        dataIndex: 'inactiveProductCount',
        width: 200,
      },
      {
        title: t('seller.brand.colActivePartnerships'),
        key: 'publisher',
        dataIndex: 'publisher',
        // width: 200,
      },
      {
        title: t('seller.brand.colAccAuthorization'),
        dataIndex: 'ccAuthorizationStatus',
        key: 'ccAuthorizationStatus',
      },

      {
        title: t('seller.brand.colAction'),
        key: 'action',
        dataIndex: 'action',
        fixed: 'right',
        width: 190,
      },
    ]
    return columnsWithPublisher
  }

  // 数据源长度大于1时显示完整列
  if (_dataSource.value?.length > 1) {
    return _columns
  } else {
    return _columns.filter((item) => item.key !== 'amazonName')
  }
})
const dataSource = ref([])
const _dataSource = ref([])
const pagination = reactive({
  total: 0,
  defaultPageSize: 10,
  size: 10,
  current: 1,
  showSizeChanger: true,
  pageSize: 10,
  pageSizeOptions: ['10', '20', '30', '40', '50'],
})

let keywords = ref('')
let status = ref('all')
let amazonUserId
const modalConfigs = ref({
  open: false,
  modalType: 'deactive',
  activeTab: '0',
})
const successfulPopConfigs = ref({
  open: false,
  modalType: 'deactive',
  activeTab: '1',
})
const showSetCommission = ref(false)
const selectSingleProduct = ref('')
const singleRecord = ref({}) //列表的单条数据
let selectedList = [] //批量操作品牌
const resetData = () => {
  return new Promise((resolve) => {
    // 重置筛选条件
    status.value = 'all'
    keywords.value = ''

    // 重置分页数据
    pagination.total = 0
    pagination.defaultPageSize = 10
    pagination.size = 10
    pagination.current = 1
    pagination.showSizeChanger = true
    pagination.pageSize = 10

    resolve()
  })
}
const reFetchFunction = async () => {
  if (activeTab.value == '0') {
    await getPartData()
  } else {
    await getAssociateBrandList()
  }
}
const handleChangeAuthorizationStatus = (record) => {
  brandRouter.push({
    path: '/seller/creatorConnections/authorization',
    query: {
      brand: true,
    },
  })
}
watch(
  activeTab,
  async () => {
    // 先重置数据
    await resetData()

    // 根据不同tab执行不同逻辑
    getData()
  },
  { immediate: true, deep: true },
)
onMounted(() => {
  amazonUserId = brandStore?.params?.amazonUserId
  if(route?.query?.type == 'attribution'){
    activeTab.value = '0'
  }
  if(route?.query?.type == 'associate' && !HIDE_ASSOCIATE_BRANDS_TAB){
    activeTab.value = '1'
  }
  // getData();
})
onUnmounted(() => {
  brandStore.setParams(null)
})
const cancelBrand = async (record) => {
  const data = await brandApi.cancelTask({
    operationId: record.pendingOperations[0].operationId,
  })
  if (data.success) {
    message.success(i18n.global.t('seller.brandsPage.cancelSuccess'), 1)
    if (activeTab.value == '0') {
      getPartData()
    } else {
      getAssociateBrandList()
    }
  }
}
const getData = async () => {
  if (activeTab.value == '0') {
    getPartData()
  } else {
    getAssociateBrandList()
  }
}
const getAssociateBrandList = (params) => {
  load.value = true
  let _params = {
    keyword: keywords.value,
    associateStatus: status.value,
    page: pagination.current,
    size: pagination.size,
    amazonUserId: amazonUserId,
  }
  let newparams = { ..._params, ...params }
  // pagination.current = newparams.page;
  newparams = Object.keys(newparams).reduce((acc, key) => {
    if (key == 'associateStatus' && newparams[key] == 'all') {
    } else if (
      newparams[key] !== null &&
      newparams[key] !== undefined &&
      newparams[key] !== ''
    ) {
      acc[key] = newparams[key]
    }
    return acc
  }, {})
  brandApi.getAssociateBrandList(newparams).then((res) => {
    load.value = false
    if (res.success) {
      if (dataSource.length) {
        document.querySelector('.brand').scrollIntoView({
          behavior: 'smooth',
        })
      }
      res.data?.data?.map((item) => {
        item.commission = item?.commissionRate || 0
        if (item.pendingOperations?.length) {
          item.timeMonth = moment(item.pendingOperations[0].displayTime)
            .tz(timezone)
            .format('MM/DD')
          item.timeMonthName = moment(item.pendingOperations[0].displayTime)
            .tz(timezone)
            .format('MMM DD,YYYY')
          item.timeMonthNameAll = moment(item.pendingOperations[0].effectiveTime)
            .tz(timezone)
            .format('MMMM DD,YYYY')
          item.timeMonthNameEOD = moment(
            item.pendingOperations[0].effectiveTime,
          )
            .tz(timezone)
            .format('MMM DD,YYYY')
        }
      })
      const brandNameCount = countBrandNames(res.data?.data, 'amazonBrandName')
      if (!_dataSource.value.length) {
        _dataSource.value = res.data?.data?.map((brand) => ({
          ...brand,
          hasDuplicate:
            brand.amazonBrandName && brandNameCount[brand.amazonBrandName] > 1,
        }))
      }
      dataSource.value = res.data?.data?.map((brand) => ({
        ...brand,
        hasDuplicate:
          brand.amazonBrandName && brandNameCount[brand.amazonBrandName] > 1,
      }))
      // console.log(dataSource.value, ' dataSource.value')
      //   dataSource.value = res.data?.data?.map(brand => ({
      //   ...brand,
      //   hasDuplicate: brand.amazonBrandName && brandNameCount[brand.amazonBrandName] > 1
      // }));
      pagination.total = res.data.total
      if (!res.data?.data?.length) {
        const status = userStore.userInfo.status
        if (status === 'SELLER_PENDING_AGREED_TO_NOTICE') {
          noData.value = true
          let _user = userStore.userInfo
          _user.status = 'NORMAL'
          userStore.setUserInfo(_user)
        } else {
          noData.value = false
        }
      }
    } else {
      const status = userStore.userInfo.status
      if (status === 'SELLER_PENDING_AGREED_TO_NOTICE') {
        noData.value = true
        let _user = userStore.userInfo
        _user.status = 'NORMAL'
        userStore.setUserInfo(_user)
      } else {
        noData.value = false
      }
    }
  })
}
const getPartData = (params) => {
  load.value = true
  let _params = {
    keyword: keywords.value,
    status: status.value,
    page: pagination.current,
    size: pagination.size,
    amazonUserId: amazonUserId,
  }
  let newparams = { ..._params, ...params }
  // pagination.current = newparams.page;
  newparams = Object.keys(newparams).reduce((acc, key) => {
    if (key == 'status' && newparams[key] == 'all') {
    } else if (
      newparams[key] !== null &&
      newparams[key] !== undefined &&
      newparams[key] !== ''
    ) {
      acc[key] = newparams[key]
    }
    return acc
  }, {})
  brandApi.getPartSellerBrands(newparams).then((res) => {
    load.value = false

    if (res.success) {
      if (dataSource.length) {
        document.querySelector('.brand').scrollIntoView({
          behavior: 'smooth',
        })
      }
      res.data?.data?.map((item) => {
        item.commission = item?.commissionRate || 0
        if (item.pendingOperations?.length) {
          item.timeMonth = moment(item.pendingOperations[0].displayTime)
            .tz(timezone)
            .format('MM/DD')
          item.timeMonthName = moment(item.pendingOperations[0].displayTime)
            .tz(timezone)
            .format('MMM DD,YYYY')
          item.timeMonthNameAll = moment(item.pendingOperations[0].displayTime)
            .tz(timezone)
            .format('MMMM DD,YYYY')
          item.timeMonthNameEOD = moment(
            item.pendingOperations[0].effectiveTime,
          )
            .tz(timezone)
            .format('MMM DD,YYYY')
        }
      })
      const brandNameCount = countBrandNames(res.data?.data, 'amazonBrandName')
      if (!_dataSource.value.length) {
        _dataSource.value = res.data?.data?.map((brand) => ({
          ...brand,
          hasDuplicate:
            brand.amazonBrandName && brandNameCount[brand.amazonBrandName] > 1,
        }))
      }

      dataSource.value = res.data?.data?.map((brand) => ({
        ...brand,
        hasDuplicate:
          brand.amazonBrandName && brandNameCount[brand.amazonBrandName] > 1,
      }))

      pagination.total = res.data.total
      if (!res.data?.data?.length) {
        const status = userStore.userInfo.status
        if (status === 'SELLER_PENDING_AGREED_TO_NOTICE') {
          noData.value = true
          let _user = userStore.userInfo
          _user.status = 'NORMAL'
          userStore.setUserInfo(_user)
        } else {
          noData.value = false
        }
      }
    } else {
      const status = userStore.userInfo.status
      if (status === 'SELLER_PENDING_AGREED_TO_NOTICE') {
        noData.value = true
        let _user = userStore.userInfo
        _user.status = 'NORMAL'
        userStore.setUserInfo(_user)
      } else {
        noData.value = false
      }
    }
  })
}

const rowSelection = ref({
  getCheckboxProps: (record) => {},
  onChange: (selectedRowKeys, selectedRows) => {
    console.log(
      `11-selectedRowKeys: ${selectedRowKeys}`,
      'selectedRows: ',
      selectedRows,
    )
  },
  onSelect: (record, selected, selectedRows) => {
    selectedList.push(record)
  },
  onSelectAll: (selected, selectedRows, changeRows) => {
    selectedList = selectedRows

    console.log('length', selectedList.length)
  },
})
const goBrandDetail = (item) => {
  const routeData = brandRouter.resolve({
    path: `detail/${item.id}`,
  })
  window.open(routeData.href, '_blank')
  // brandRouter.push({
  //   name:'brandDetail',
  //   params:{
  //     id:item.id,
  //   }
  // })
}
const goProductPage = (item, type) => {
  let params = {
    brandName: item.amazonBrandName,
    brandId: item.id,
    activeTabInfo: activeTab.value,
  }
  if (type == 'active') {
    params.status = 'active'
  } else {
    params.status = 'inactive'
  }
  productStore.setParams(params)
  brandRouter.push({
    name: 'product',
    query: params,
  })
}

const onTableChange = ({ current, pageSize }) => {
  // console.log('current', current, 'pageSize', pageSize)
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  getData({ page: current, size: pageSize })
}
const onPageChange = (current) => {
  pagination.current = current
  getData({ page: current })
}
const onSearch = () => {
  getData({ keyword: keywords.value })
}
const onStatusChange = (key) => {
  status.value = key
  getData({ status: key })
}
const onOpenModal = (record, modalType) => {
  dataSource.value.map((v) => (v.visible = false))
  singleRecord.value = record
  switch (modalType) {
    case 'deactive':
      modalConfigs.value.modalType = 'deactive'
      modalConfigs.value.open = true
      modalConfigs.value.activeTab = activeTab.value
      break
    case 'setCommission':
      showSetCommission.value = true
      setSelectSingleProduct(record.id)
      break
    case 'active':
      modalConfigs.value.modalType = 'active'
      modalConfigs.value.open = true
      modalConfigs.value.activeTab = activeTab.value
      break
    case 'setPublisher':
     let name = record.amazonBrandName
      if (record.hasDuplicate) {
        name =
          record.amazonBrandName +
          '(' +
          record.amazonProfileAccountInfoName +
          ')'
      }
      const param = {
        id: record.id,
        // creatorName:record.amazonBrandName,
        name: name,
        publicImage: record.logoImageUrl,
        type: 'publisher',
        // inactiveSelectedRowKeys: inactiveSelectedRowKeys,
        selectType: 'single',
      }
      sessionStorage.setItem('brandDetail', JSON.stringify(param))
      brandRouter.push({
        path:'/seller/associateCentral/setUpAssociatePartnershipBrands?isAcc=false',
      })
      // window.open(`/seller/associateCentral/setUpAssociatePartnershipBrands?isAcc=false`)
      // partnershipModal.value.show(
      //   record.id,
      //   name,
      //   record.amazonBrandName,
      //   record.logoImageUrl,
      //   'publisher',
      //   'single'
      // )
      break
  }
}

const setSelectSingleProduct = (id) => {
  selectSingleProduct.value = id
}
const setUpAssociateCommission = () => {
  successfulPopConfigs.value.open = false

  if (successfulPopConfigs.value.modalType == 'deactive') {
    onOpenModal(singleRecord.value, 'setPublisher')
  }
  // onOpenModal(singleRecord.value, 'setPublisher')
}
const onUpAssociateCommissioModalOK = () => {
  successfulPopConfigs.value.open = false
  getData()
}
const onUpAssociateCommissioModalCancel = () => {
  successfulPopConfigs.value.open = false
}
const onModalOK = (item) => {
  switch (modalConfigs.value.modalType) {
    case 'deactive':
      onDeactiveBrand(item)
      break
    case 'setCommission':
      onSetCommission(item)
      break
    case 'active':
      onActiveBrand(item)
      break
  }
}
const onModalCancel = () => {
  modalConfigs.value.open = false
}
const onDeactiveBrand = (item) => {
  modalConfigs.value.open = false
  if (activeTab.value == '0') {
    brandApi.disableBrand({ id: item.id }).then((res) => {
      if (res.success) {
        successfulPopConfigs.value.open = true
        successfulPopConfigs.value.activeTab = activeTab.value
        successfulPopConfigs.value.modalType = 'active'
        successInfo.value = res?.data
        getData()
        message.success(i18n.global.t('seller.msg.success'), 1)
      } else {
        openNotification(res?.error)
      }
    })
  } else {
    brandApi.disableBrandAssociate({ id: item.id }).then((res) => {
      if (res.success) {
        successfulPopConfigs.value.open = true
        successfulPopConfigs.value.activeTab = activeTab.value
        successfulPopConfigs.value.modalType = 'active'
        successInfo.value = res?.data
        getData()
        message.success(i18n.global.t('seller.msg.success'), 1)
      } else {
        openNotification(res?.error)
      }
    })
  }
}
const setShowSetCommission = (status) => {
  showSetCommission.value = status
}
const onActiveBrand = (item) => {
  modalConfigs.value.open = false
  if (activeTab.value == '0') {
    let req = {
      id: item.id,
      commissionRate: item.commission,
    }
    brandApi.setBrandPer(req).then((res) => {
      if (res.success) {
        brandApi.activateBrand({ id: item.id }).then((res) => {
      if (res.success) {
        successfulPopConfigs.value.open = true
        successfulPopConfigs.value.activeTab = activeTab.value
        successfulPopConfigs.value.modalType = 'deactive'
        successInfo.value = res?.data
        getData()
        message.success(i18n.global.t('seller.msg.success'), 1)
      } else {
        openNotification(res?.error)
      }
    })
      }
    })
   
  } else {
    brandApi.activateBrandAssociate({ id: item.id }).then((res) => {
      if (res.success) {
        successfulPopConfigs.value.open = true
        successfulPopConfigs.value.activeTab = activeTab.value
        successfulPopConfigs.value.modalType = 'deactive'
        successInfo.value = res?.data
        getData()
        message.success(i18n.global.t('seller.msg.success'), 1)
      } else {
        openNotification(res?.error)
      }
    })
  }
}

// 发布者模态框相关
const publishModalRef = ref(null)
const openPublisherModal = (record) => {
  publishModalRef.value?.open(record.id)
}
// setTimeout(()=>{
//   setUpPartnershipRef.value?.open()
// },1000)
</script>
<style lang="less" scoped>
@import '@/assets/less/ellipsis.less';
.action-btn {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.asin-vc-icon {
  display: flex;
  align-items: center;
  // justify-content: flex-end;
  gap: 4px;
}

.time {
  background: rgba(245, 246, 247, 1);
  border-radius: 6px;
  padding: 4px 8px;
  margin-top: 10px;
  display: inline-block;
  .time-mid {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.time-month {
  font-family: Sora;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  color: rgba(15, 6, 41, 0.7);
  letter-spacing: 0.4px;
}

.close-icon {
  font-size: 13px;
  color: rgba(15, 6, 41, 0.2);
}

.brand-title {
  display: flex;
  align-items: center;

  .brand-font {
    word-break: break-all;
    flex: 1;
    margin-left: 12px;
    font-weight: 500;
    color: var(--dark-1);
    letter-spacing: 0.4px;
    line-height: 22px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    text-decoration: underline;
  }

  .brand-img-show {
    width: 56px;
    height: 56px;
    display: flex;
    border-radius: 8px;
    align-items: center;
    background: var(--bg-gray-2);

    img {
      border-radius: 8px;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .brand-name {
      margin: 0 auto;
      padding: 0 4px;
      max-width: 56px;
      font-size: 11px;
      overflow: hidden;
      max-height: 39px;
      font-weight: 600;
      line-height: 13px;
      text-align: center;
      color: var(--dark-4);
      letter-spacing: 0.3px;
    }
  }
}

.table-font {
  font-family: Sora, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: var(--dark-1);
  letter-spacing: 0.4px;
  line-height: 19px;
  cursor: pointer;
  text-decoration: underline;
}

.action-font {
  display: inline-block;
  cursor: pointer;
  border: 1px solid var(--border-1);
  height: 24px;
  border-radius: 8px;
  padding: 0 8px;
  opacity: 0.7;
  font-family: Sora, sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: var(--dark-1);
  letter-spacing: 0.3px;
  line-height: 24px;
}

.brand {
  padding: 16px;
  padding-top: unset;
}

.table {
  padding: 0 16px;
  // margin: 0 16px;
  background-color: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
  // border: 2px solid #E8E5ED;
}

.table-head {
  display: flex;
  height: 64px;
  justify-content: space-between;
  align-items: center;

  .filters {
    display: flex;
    justify-content: space-between;

    .statusFilter {
      position: relative;
      z-index: 2;
      margin-left: 18px;
      // opacity: 0.7;
      font-family: Sora, sans-serif;
      font-weight: 400;
      font-size: 14px;
      color: var(--dark-2);
      letter-spacing: 0.4px;
      line-height: 20px;
    }
  }
}

.popoverContent {
  font-family: Sora;
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
}

.contentBox {
  margin-top: -7px !important;
  cursor: pointer;
}

.cancelBtn {
  cursor: pointer;
  margin-top: 10px;
  max-width: 70px;
  font-family: Sora;
  font-weight: 500;
  font-size: 13px;
  color: rgba(235, 78, 61, 1);
  line-height: 18px;
  letter-spacing: 0.3px;
  padding: 3px 11px;
  text-align: center;
  background-color: rgba(254, 238, 236, 1);
  border: 1px solid rgba(245, 119, 105, 1);
  border-radius: 6px;
}
</style>
